import { motion } from 'framer-motion';
import {
  Copy,
  Download,
  FileText,
  FolderOpen,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';

import {
  getTokensForHighlighting,
  parseAndEvaluate,
  type EvaluationContext,
} from '../utils/textCalciParser';

interface Line {
  id: string;
  input: string;
  result: string | null;
  error: boolean;
  errorMessage?: string;
}

interface SavedFile {
  name: string;
  lines: Line[];
  timestamp: string;
}

interface EvaluatedLine {
  line: Line;
  result: string | null;
  error: boolean;
  errorMessage?: string;
  variableAssignment?: { name: string; value: number };
}

// Helper function to evaluate a single line using the parser
const evaluateLineWithTransforms = (
  line: Line,
  tempVars: Record<string, number>,
  allLines: Line[],
  currentLineIndex: number
): EvaluatedLine => {
  const trimmed = line.input.trim();

  // Skip empty lines and comments
  if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('//')) {
    return { line, result: null, error: false };
  }

  try {
    const context: EvaluationContext = {
      variables: tempVars,
      lines: allLines,
      lineIndex: currentLineIndex,
    };

    const result = parseAndEvaluate(line.input, context);

    if ('error' in result) {
      // Skip "empty or comment" errors
      if (result.error === 'empty or comment') {
        return { line, result: null, error: false };
      }
      return { line, result: null, error: true, errorMessage: result.error };
    }

    return {
      line,
      result: result.value.toString(),
      error: false,
      variableAssignment: result.variableName
        ? { name: result.variableName, value: result.value }
        : undefined,
    };
  } catch (e) {
    // Swallow the exception and return error state for UI
    const errorMsg = e instanceof Error ? e.message : 'Unknown error';
    return { line, result: null, error: true, errorMessage: errorMsg };
  }
};

const TextCalci: React.FC = () => {
  useEffect(() => {
    document.title = 'TextCalci | NiveshRight';
  }, []);

  const [currentFileName, setCurrentFileName] = useState<string>('Untitled');
  const [isEditingFileName, setIsEditingFileName] = useState(false);
  const [lines, setLines] = useState<Line[]>([
    { id: '1', input: '', result: null, error: false },
  ]);
  const [variables, setVariables] = useState<Record<string, number>>({});
  // Track variable values at each line for accurate tooltips
  const [lineVariables, setLineVariables] = useState<
    Record<string, Record<string, number>>
  >({});
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [savedFiles, setSavedFiles] = useState<SavedFile[]>([]);
  const [showFileList, setShowFileList] = useState(false);
  const [autocompleteOptions, setAutocompleteOptions] = useState<string[]>([]);
  const [autocompletePosition, setAutocompletePosition] = useState<{
    lineId: string;
    top: number;
    left: number;
  } | null>(null);
  const [selectedAutocompleteIndex, setSelectedAutocompleteIndex] = useState(0);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  // Show toast notification
  const showToast = (
    message: string,
    type: 'success' | 'error' | 'info' = 'success'
  ) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Syntax highlight helper - highlights variable names and line references
  const highlightSyntax = (text: string, lineId: string) => {
    if (!text) return null;

    // If it's a comment, return as-is (will be styled by parent)
    if (text.trim().startsWith('#') || text.trim().startsWith('//')) {
      return <span>{text}</span>;
    }

    try {
      // Get tokens from the parser
      const tokens = getTokensForHighlighting(text);

      // Get variables available at this specific line for tooltips
      const varsAtLine = lineVariables[lineId] || {};

      const parts: React.ReactNode[] = [];
      let lastEnd = 0;

      tokens.forEach((token, idx) => {
        // Add text before token (if any)
        if (token.startOffset !== undefined && token.startOffset > lastEnd) {
          parts.push(
            <span key={`text-${lastEnd}`}>
              {text.substring(lastEnd, token.startOffset)}
            </span>
          );
        }

        // Determine styling based on token type
        let className = '';
        let title = '';

        switch (token.type) {
          case 'Identifier':
            // Check if it's a variable
            if (varsAtLine[token.image]) {
              className =
                'text-blue-400 cursor-help border-b border-dotted border-blue-400/50 hover:border-blue-400 pointer-events-auto';
              title = `${token.image} = ${varsAtLine[token.image]}`;
            }
            break;
          case 'LineReference':
            className =
              'text-purple-400 cursor-help border-b border-dotted border-purple-400/50 hover:border-purple-400 pointer-events-auto';
            const lineNum = token.image.match(/@(?:line)?(\d+)/i)?.[1];
            const lineIndex = lineNum ? parseInt(lineNum) - 1 : -1;
            const refValue =
              lineIndex >= 0 && lineIndex < lines.length
                ? lines[lineIndex].result || 'N/A'
                : 'N/A';
            title = `Line ${lineNum} = ${refValue}`;
            break;
          case 'CurrencySymbol':
          case 'CurrencyCode':
          case 'CurrencyWord':
            className = 'text-green-400';
            break;
          case 'IndianNumberAbbrev':
          case 'IndianNumberWord':
          case 'WesternNumberAbbrev':
          case 'WesternNumberWord':
            className = 'text-yellow-400';
            break;
          case 'PercentOf':
          case 'PercentOff':
            className = 'text-pink-400';
            break;
          case 'Number':
            className = 'text-cyan-400';
            break;
        }

        parts.push(
          <span
            key={`token-${idx}-${token.startOffset}`}
            className={className}
            title={title}
          >
            {token.image}
          </span>
        );

        lastEnd = token.endOffset !== undefined ? token.endOffset + 1 : lastEnd;
      });

      // Add remaining text
      if (lastEnd < text.length) {
        parts.push(
          <span key={`text-${lastEnd}`}>{text.substring(lastEnd)}</span>
        );
      }

      return <>{parts}</>;
    } catch (error) {
      // Fallback: just return plain text if highlighting fails (swallow error)
      return <span>{text}</span>;
    }
  };

  // Load saved files list from localStorage on mount
  useEffect(() => {
    const savedFilesData = localStorage.getItem('textCalci-files');
    if (savedFilesData) {
      try {
        const files = JSON.parse(savedFilesData);
        setSavedFiles(files);
      } catch (e) {
        // Swallow error - localStorage might be corrupted, just start fresh
        localStorage.removeItem('textCalci-files');
      }
    }

    // Load current file
    const currentFile = localStorage.getItem('textCalci-current');
    if (currentFile) {
      try {
        const parsed = JSON.parse(currentFile);
        setCurrentFileName(parsed.name || 'Untitled');
        const loadedLines = parsed.lines || [
          { id: '1', input: '', result: null, error: false },
        ];
        setLines(loadedLines);
        setLastSaved(parsed.timestamp ? new Date(parsed.timestamp) : null);

        // Re-evaluate all lines to rebuild variables state
        setTimeout(() => {
          const tempVars: Record<string, number> = {};
          const evaluatedLines: Line[] = [];
          const newLineVariables: Record<string, Record<string, number>> = {};

          // Evaluate lines sequentially, building variables as we go
          for (let i = 0; i < loadedLines.length; i++) {
            const line = loadedLines[i];
            const evaluated = evaluateLineWithTransforms(
              line,
              tempVars,
              evaluatedLines,
              i
            );

            // Update tempVars if this was a variable assignment
            if (evaluated.variableAssignment) {
              tempVars[evaluated.variableAssignment.name] =
                evaluated.variableAssignment.value;
            }

            // Store variable state for this line (after assignment)
            newLineVariables[line.id] = { ...tempVars };

            evaluatedLines.push({
              ...evaluated.line,
              result: evaluated.result,
              error: evaluated.error,
              errorMessage: evaluated.errorMessage,
            });
          }

          setVariables(tempVars);
          setLineVariables(newLineVariables);
          setLines(evaluatedLines);
        }, 100);
      } catch (e) {
        // Swallow error - localStorage might be corrupted, just start fresh
        localStorage.removeItem('textCalci-current');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save current file to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      const content = {
        name: currentFileName,
        lines: lines,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('textCalci-current', JSON.stringify(content));
      setLastSaved(new Date());
    }, 1000);

    return () => clearTimeout(timer);
  }, [lines, currentFileName]);

  const handleLineChange = (lineId: string, value: string) => {
    // Check for autocomplete trigger
    const cursorPosition = textareaRefs.current[lineId]?.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const match = textBeforeCursor.match(/(\w+)$/);

    if (match && match[1].length > 0) {
      const partialWord = match[1];

      // Available suggestions: variables + keywords
      const keywords = [
        'lakh',
        'lakhs',
        'crore',
        'crores',
        'thousand',
        'thousands',
        'million',
        'millions',
        'billion',
        'billions',
        'trillion',
        'trillions',
        'of',
        'off',
        'rupees',
        'dollars',
        'euros',
        'pounds',
      ];

      const availableVars = Object.keys(variables);
      const allSuggestions = [...availableVars, ...keywords];

      const filtered = allSuggestions.filter(
        item =>
          item.toLowerCase().startsWith(partialWord.toLowerCase()) &&
          item.toLowerCase() !== partialWord.toLowerCase()
      );

      if (filtered.length > 0) {
        // Sort: variables first, then keywords
        const sortedFiltered = filtered.sort((a, b) => {
          const aIsVar = availableVars.includes(a);
          const bIsVar = availableVars.includes(b);
          if (aIsVar && !bIsVar) return -1;
          if (!aIsVar && bIsVar) return 1;
          return a.localeCompare(b);
        });

        setAutocompleteOptions(sortedFiltered);
        setSelectedAutocompleteIndex(0);
        setAutocompletePosition({ lineId, top: 0, left: 0 });
      } else {
        setAutocompleteOptions([]);
        setAutocompletePosition(null);
      }
    } else {
      setAutocompleteOptions([]);
      setAutocompletePosition(null);
    }

    setLines(prevLines => {
      // First update the changed line
      const newLines = prevLines.map(line => {
        if (line.id === lineId) {
          return { ...line, input: value };
        }
        return line;
      });

      // Re-evaluate all lines with proper variable dependency handling
      const tempVars: Record<string, number> = {};
      const updatedLines: Line[] = [];
      const newLineVariables: Record<string, Record<string, number>> = {};

      for (let i = 0; i < newLines.length; i++) {
        const line = newLines[i];
        const evaluated = evaluateLineWithTransforms(
          line,
          tempVars,
          updatedLines,
          i
        );

        // Update tempVars if this was a variable assignment
        if (evaluated.variableAssignment) {
          tempVars[evaluated.variableAssignment.name] =
            evaluated.variableAssignment.value;
        }

        // Store variable state for this line (after assignment)
        // This ensures the line can see all variables available at this point
        newLineVariables[line.id] = { ...tempVars };

        updatedLines.push({
          ...evaluated.line,
          result: evaluated.result,
          error: evaluated.error,
          errorMessage: evaluated.errorMessage,
        });
      }

      // Update variables state and line-specific variables
      setVariables(tempVars);
      setLineVariables(newLineVariables);

      return updatedLines;
    });
  };

  const handleKeyDown = (
    lineId: string,
    e: KeyboardEvent<HTMLTextAreaElement>
  ) => {
    const currentIndex = lines.findIndex(l => l.id === lineId);

    // Handle autocomplete navigation
    if (
      autocompleteOptions.length > 0 &&
      autocompletePosition?.lineId === lineId
    ) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev < autocompleteOptions.length - 1 ? prev + 1 : 0
        );
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedAutocompleteIndex(prev =>
          prev > 0 ? prev - 1 : autocompleteOptions.length - 1
        );
        return;
      } else if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault();
        const selectedVar = autocompleteOptions[selectedAutocompleteIndex];
        const textarea = textareaRefs.current[lineId];
        if (textarea) {
          const cursorPos = textarea.selectionStart;
          const currentValue = lines[currentIndex].input;
          const textBeforeCursor = currentValue.substring(0, cursorPos);
          const textAfterCursor = currentValue.substring(cursorPos);
          const match = textBeforeCursor.match(/(\w+)$/);

          if (match) {
            const partialWord = match[1];
            const newText =
              textBeforeCursor.slice(0, -partialWord.length) +
              selectedVar +
              textAfterCursor;
            handleLineChange(lineId, newText);

            // Set cursor position after the inserted variable
            setTimeout(() => {
              const newCursorPos =
                cursorPos - partialWord.length + selectedVar.length;
              textarea.setSelectionRange(newCursorPos, newCursorPos);
            }, 0);
          }
        }
        setAutocompleteOptions([]);
        setAutocompletePosition(null);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setAutocompleteOptions([]);
        setAutocompletePosition(null);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const newLine = {
        id: Date.now().toString(),
        input: '',
        result: null,
        error: false,
      };
      setLines(prev => [
        ...prev.slice(0, currentIndex + 1),
        newLine,
        ...prev.slice(currentIndex + 1),
      ]);

      // Focus the new line
      setTimeout(() => {
        textareaRefs.current[newLine.id]?.focus();
      }, 0);
    } else if (
      e.key === 'Backspace' &&
      lines[currentIndex].input === '' &&
      lines.length > 1
    ) {
      e.preventDefault();
      setLines(prev => prev.filter(l => l.id !== lineId));

      // Focus previous line
      if (currentIndex > 0) {
        const prevLine = lines[currentIndex - 1];
        setTimeout(() => {
          textareaRefs.current[prevLine.id]?.focus();
        }, 0);
      }
    } else if (e.key === 'ArrowUp' && currentIndex > 0) {
      e.preventDefault();
      const prevLine = lines[currentIndex - 1];
      textareaRefs.current[prevLine.id]?.focus();
    } else if (e.key === 'ArrowDown' && currentIndex < lines.length - 1) {
      e.preventDefault();
      const nextLine = lines[currentIndex + 1];
      textareaRefs.current[nextLine.id]?.focus();
    }
  };

  const saveFile = () => {
    const file: SavedFile = {
      name: currentFileName,
      lines: lines,
      timestamp: new Date().toISOString(),
    };

    const updatedFiles = savedFiles.filter(f => f.name !== currentFileName);
    updatedFiles.push(file);
    setSavedFiles(updatedFiles);
    localStorage.setItem('textCalci-files', JSON.stringify(updatedFiles));
    showToast(`File "${currentFileName}" saved successfully!`);
  };

  const createNewFile = () => {
    // Check if there's any content in the current file
    const hasContent = lines.some(line => line.input.trim() !== '');

    // Check if the current file is already saved
    const currentFileIsSaved = savedFiles.some(
      file =>
        file.name === currentFileName &&
        JSON.stringify(file.lines) === JSON.stringify(lines)
    );

    // Only prompt if there's unsaved content
    if (hasContent && !currentFileIsSaved) {
      if (
        !window.confirm(
          'Create new file? Current unsaved changes will be saved to current file.'
        )
      ) {
        return;
      }
      saveFile();
    }

    setCurrentFileName('Untitled');
    setLines([
      { id: Date.now().toString(), input: '', result: null, error: false },
    ]);
    setVariables({});
    setLastSaved(null);
  };

  const openFile = (file: SavedFile) => {
    // Check if there's any unsaved content in the current file
    const hasContent = lines.some(line => line.input.trim() !== '');
    const currentFileIsSaved = savedFiles.some(
      f =>
        f.name === currentFileName &&
        JSON.stringify(f.lines) === JSON.stringify(lines)
    );

    // Prompt if there are unsaved changes
    if (hasContent && !currentFileIsSaved) {
      if (
        !window.confirm(
          'Open file? Current unsaved changes will be saved to current file.'
        )
      ) {
        return;
      }
      saveFile();
    }

    setCurrentFileName(file.name);
    setLines(file.lines);
    setVariables({});
    setLastSaved(new Date(file.timestamp));
    setShowFileList(false);

    // Re-evaluate all lines to rebuild variables state
    setTimeout(() => {
      const tempVars: Record<string, number> = {};
      const evaluatedLines: Line[] = [];
      const newLineVariables: Record<string, Record<string, number>> = {};

      // Evaluate lines sequentially, building variables as we go
      for (let i = 0; i < file.lines.length; i++) {
        const line = file.lines[i];
        const evaluated = evaluateLineWithTransforms(
          line,
          tempVars,
          evaluatedLines,
          i
        );

        // Update tempVars if this was a variable assignment
        if (evaluated.variableAssignment) {
          tempVars[evaluated.variableAssignment.name] =
            evaluated.variableAssignment.value;
        }

        // Store variable state for this line (after assignment)
        newLineVariables[line.id] = { ...tempVars };

        evaluatedLines.push({
          ...evaluated.line,
          result: evaluated.result,
          error: evaluated.error,
          errorMessage: evaluated.errorMessage,
        });
      }

      setVariables(tempVars);
      setLineVariables(newLineVariables);
      setLines(evaluatedLines);
    }, 100);
  };

  const deleteFile = (fileName: string) => {
    if (window.confirm(`Delete file "${fileName}"?`)) {
      const updatedFiles = savedFiles.filter(f => f.name !== fileName);
      setSavedFiles(updatedFiles);
      localStorage.setItem('textCalci-files', JSON.stringify(updatedFiles));
    }
  };

  const clearAll = () => {
    if (window.confirm('Clear all calculations?')) {
      setLines([
        { id: Date.now().toString(), input: '', result: null, error: false },
      ]);
      setVariables({});
      setLastSaved(null);
    }
  };

  const copyToClipboard = () => {
    const text = lines
      .map(line => {
        if (!line.input.trim()) return '';

        // Skip comments and lines without results
        if (
          !line.result ||
          line.input.trim().startsWith('#') ||
          line.input.trim().startsWith('//')
        ) {
          return line.input;
        }

        // All results should be shown as comments
        return `${line.input}  # = ${line.result}`;
      })
      .filter(Boolean)
      .join('\n');

    navigator.clipboard.writeText(text);
    showToast('Copied to clipboard!');
  };

  const exportToFile = () => {
    const content = lines
      .map(line => {
        if (!line.input.trim()) return '';

        // Skip comments and lines without results
        if (
          !line.result ||
          line.input.trim().startsWith('#') ||
          line.input.trim().startsWith('//')
        ) {
          return line.input;
        }

        // All results should be shown as comments
        return `${line.input}  # = ${line.result}`;
      })
      .filter(Boolean)
      .join('\n');

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `textcalci-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <main className="w-full px-2 sm:px-4 lg:px-8">
        {/* File Management Bar - Editor-style Toolbar */}
        <div className="mb-1 bg-[#2d2d2d] rounded-lg shadow-lg overflow-hidden">
          {/* File Name Tab */}
          <div className="flex items-center gap-2 px-3 py-2 bg-[#252526] border-b border-[#1e1e1e]">
            <FileText className="w-4 h-4 text-gray-400 shrink-0" />
            {isEditingFileName ? (
              <input
                type="text"
                value={currentFileName}
                onChange={e => setCurrentFileName(e.target.value)}
                onBlur={() => setIsEditingFileName(false)}
                onKeyDown={e => {
                  if (e.key === 'Enter') setIsEditingFileName(false);
                }}
                className="px-2 py-1 bg-[#3c3c3c] border border-[#007acc] rounded text-white text-xs md:text-sm focus:outline-none focus:ring-1 focus:ring-[#007acc]"
                autoFocus
              />
            ) : (
              <button
                onClick={() => setIsEditingFileName(true)}
                className="px-2 py-1 hover:bg-[#3c3c3c] rounded text-white text-xs md:text-sm font-normal transition-colors max-w-xs truncate"
                title="Click to rename"
              >
                {currentFileName}
              </button>
            )}
            {lastSaved && (
              <span className="text-[10px] md:text-xs text-gray-500 ml-auto whitespace-nowrap">
                <span className="hidden sm:inline">Auto-saved </span>
                <span className="sm:hidden">Saved </span>
                {lastSaved.toLocaleTimeString()}
              </span>
            )}
          </div>

          {/* Toolbar Actions */}
          <div className="flex items-center gap-0.5 px-2 py-1.5 overflow-x-auto bg-[#2d2d2d]">
            <button
              onClick={createNewFile}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-gray-300 hover:bg-[#3c3c3c] rounded transition-colors whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span>New</span>
            </button>
            <button
              onClick={() => setShowFileList(!showFileList)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-gray-300 hover:bg-[#3c3c3c] rounded transition-colors whitespace-nowrap"
            >
              <FolderOpen className="w-3.5 h-3.5 shrink-0" />
              <span>Open</span>
            </button>
            <button
              onClick={saveFile}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-gray-300 hover:bg-[#3c3c3c] rounded transition-colors whitespace-nowrap"
            >
              <Save className="w-3.5 h-3.5 shrink-0" />
              <span>Save</span>
            </button>
            <div className="w-px h-4 bg-[#3c3c3c] mx-1" />
            <button
              onClick={copyToClipboard}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-gray-300 hover:bg-[#3c3c3c] rounded transition-colors whitespace-nowrap"
            >
              <Copy className="w-3.5 h-3.5 shrink-0" />
              <span>Copy</span>
            </button>
            <button
              onClick={exportToFile}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-gray-300 hover:bg-[#3c3c3c] rounded transition-colors whitespace-nowrap"
            >
              <Download className="w-3.5 h-3.5 shrink-0" />
              <span>Export</span>
            </button>
            <div className="w-px h-4 bg-[#3c3c3c] mx-1" />
            <button
              onClick={clearAll}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] md:text-xs text-red-400 hover:bg-[#3c3c3c] hover:text-red-300 rounded transition-colors whitespace-nowrap"
            >
              <Trash2 className="w-3.5 h-3.5 shrink-0" />
              <span>Clear</span>
            </button>
          </div>

          {/* File List Dropdown */}
          {showFileList && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-[#1e1e1e] bg-[#252526]"
            >
              <div className="px-3 py-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                  Saved Files
                </h3>
                {savedFiles.length === 0 ? (
                  <p className="text-xs text-gray-500 px-1 py-2">
                    No saved files yet
                  </p>
                ) : (
                  <div className="space-y-0.5">
                    {savedFiles.map(file => (
                      <div
                        key={file.name}
                        className="flex items-center justify-between p-2 rounded hover:bg-[#3c3c3c] transition-colors group"
                      >
                        <button
                          onClick={() => openFile(file)}
                          className="flex-1 text-left flex items-center gap-2 min-w-0"
                        >
                          <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-300 truncate">
                              {file.name}
                            </div>
                            <div className="text-[10px] text-gray-500">
                              {new Date(file.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </button>
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            deleteFile(file.name);
                          }}
                          className="p-1.5 hover:bg-red-900/30 rounded text-red-400 hover:text-red-300 transition-colors opacity-100 md:opacity-0 md:group-hover:opacity-100 shrink-0"
                          title="Delete file"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Calculator Interface */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Editor Area */}
          <div className="min-h-[300px] md:min-h-[500px] bg-[#1e1e1e] font-mono text-sm md:text-[15px]">
            {lines.map((line, index) => (
              <motion.div
                key={line.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex hover:bg-[#2a2a2a] transition-colors"
              >
                {/* Line Number */}
                <div className="bg-[#1a1a1a] border-r border-[#333] select-none w-10 md:w-14 text-right pr-3 md:pr-4 py-2 md:py-3 text-gray-600 text-xs md:text-sm hover:text-gray-400 transition-colors shrink-0">
                  {index + 1}
                </div>

                {/* Line Content */}
                <div className="flex-1">
                  <div className="flex items-start">
                    {/* Input Area */}
                    <div className="flex-1 py-2 md:py-2 pr-2 md:pr-4 pl-3 md:pl-4 relative">
                      {/* Syntax-highlighted overlay */}
                      <div
                        className={`absolute inset-0 py-2 md:py-2 pr-2 md:pr-4 pl-3 md:pl-4 pointer-events-none font-mono text-sm md:text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                          line.error
                            ? 'text-red-400'
                            : line.input.trim().startsWith('#') ||
                                line.input.trim().startsWith('//')
                              ? 'text-gray-500 italic'
                              : 'text-gray-100'
                        }`}
                        style={{
                          minHeight: '26px',
                          maxHeight: '200px',
                          overflow: 'hidden',
                        }}
                      >
                        {highlightSyntax(line.input, line.id)}
                      </div>
                      {/* Textarea (invisible text) */}
                      <textarea
                        ref={el => (textareaRefs.current[line.id] = el)}
                        value={line.input}
                        onChange={e =>
                          handleLineChange(line.id, e.target.value)
                        }
                        onKeyDown={e => handleKeyDown(line.id, e)}
                        placeholder={
                          lines.length === 1 && !line.input
                            ? 'Type your calculation...'
                            : ''
                        }
                        rows={1}
                        className="w-full bg-transparent text-transparent caret-white placeholder-gray-600 focus:outline-none resize-none leading-relaxed"
                        style={{
                          minHeight: '26px',
                          maxHeight: '200px',
                          overflow: 'auto',
                        }}
                      />
                      {/* Autocomplete Dropdown */}
                      {autocompleteOptions.length > 0 &&
                        autocompletePosition?.lineId === line.id && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="absolute left-0 top-full mt-1 bg-[#2a2a2a] border border-gray-600 rounded-md shadow-lg z-50 min-w-[200px]"
                          >
                            {autocompleteOptions.map((option, idx) => (
                              <div
                                key={option}
                                className={`px-3 py-2 cursor-pointer flex items-center justify-between ${
                                  idx === selectedAutocompleteIndex
                                    ? 'bg-blue-600 text-white'
                                    : 'text-gray-300 hover:bg-[#3a3a3a]'
                                }`}
                                onClick={() => {
                                  const selectedVar = option;
                                  const textarea =
                                    textareaRefs.current[line.id];
                                  if (textarea) {
                                    const cursorPos = textarea.selectionStart;
                                    const currentValue = line.input;
                                    const textBeforeCursor =
                                      currentValue.substring(0, cursorPos);
                                    const textAfterCursor =
                                      currentValue.substring(cursorPos);
                                    const match =
                                      textBeforeCursor.match(/(\w+)$/);

                                    if (match) {
                                      const partialWord = match[1];
                                      const newText =
                                        textBeforeCursor.slice(
                                          0,
                                          -partialWord.length
                                        ) +
                                        selectedVar +
                                        textAfterCursor;
                                      handleLineChange(line.id, newText);

                                      setTimeout(() => {
                                        const newCursorPos =
                                          cursorPos -
                                          partialWord.length +
                                          selectedVar.length;
                                        textarea.setSelectionRange(
                                          newCursorPos,
                                          newCursorPos
                                        );
                                        textarea.focus();
                                      }, 0);
                                    }
                                  }
                                  setAutocompleteOptions([]);
                                  setAutocompletePosition(null);
                                }}
                              >
                                <span className="font-mono">{option}</span>
                                <span className="text-xs opacity-70 ml-2">
                                  {variables[option] !== undefined
                                    ? `= ${variables[option]}`
                                    : 'keyword'}
                                </span>
                              </div>
                            ))}
                            <div className="px-3 py-1 text-xs text-gray-500 border-t border-gray-600">
                              ↑↓ navigate • Tab/Enter select • Esc close
                            </div>
                          </motion.div>
                        )}
                    </div>

                    {/* Result Area - Desktop (right side) */}
                    <div className="hidden md:block w-80 py-3 pr-6 text-right shrink-0">
                      {line.result && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="text-green-400 font-semibold cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            const textarea = textareaRefs.current[line.id];
                            if (textarea) {
                              textarea.focus();
                              const length = line.input.length;
                              textarea.setSelectionRange(length, length);
                            }
                          }}
                        >
                          = {line.result}
                        </motion.div>
                      )}
                      {line.error && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          className="text-red-400 text-sm cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            const textarea = textareaRefs.current[line.id];
                            if (textarea) {
                              textarea.focus();
                              const length = line.input.length;
                              textarea.setSelectionRange(length, length);
                            }
                          }}
                          title={line.errorMessage}
                        >
                          {line.errorMessage || 'error'}
                        </motion.div>
                      )}
                    </div>
                  </div>

                  {/* Result Area - Mobile (below input) */}
                  {(line.result || line.error) && (
                    <div className="md:hidden pl-3 md:pl-4 pr-3 pb-2">
                      {line.result && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-green-400 font-semibold text-xs cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            const textarea = textareaRefs.current[line.id];
                            if (textarea) {
                              textarea.focus();
                              const length = line.input.length;
                              textarea.setSelectionRange(length, length);
                            }
                          }}
                        >
                          = {line.result}
                        </motion.div>
                      )}
                      {line.error && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-400 text-xs cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            const textarea = textareaRefs.current[line.id];
                            if (textarea) {
                              textarea.focus();
                              const length = line.input.length;
                              textarea.setSelectionRange(length, length);
                            }
                          }}
                          title={line.errorMessage}
                        >
                          {line.errorMessage || 'error'}
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            How to use TextCalci
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Basic operations
              </h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  • Addition:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    50000 + 25000
                  </code>
                </li>
                <li>
                  • Subtraction:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    100000 - 30000
                  </code>
                </li>
                <li>
                  • Multiplication:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    5000 * 12
                  </code>
                </li>
                <li>
                  • Division:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    1200000 / 12
                  </code>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Percentages</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  • Calculate:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    20% of 50000
                  </code>
                </li>
                <li>
                  • Discount:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    15% off 1000
                  </code>
                </li>
                <li>
                  • Increase:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    50000 + 10%
                  </code>
                </li>
                <li>
                  • Decrease:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    50000 - 5%
                  </code>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Variables</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  • Assign:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    salary = 50000
                  </code>
                </li>
                <li>
                  • Use:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    salary * 12
                  </code>
                </li>
                <li>
                  • Re-assign:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    salary = salary * 1.1
                  </code>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Line references
              </h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  • Reference:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">@line3</code>
                  {' or '}
                  <code className="bg-white px-2 py-0.5 rounded">@3</code>
                </li>
                <li>
                  • Use in calc:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">@2 + @3</code>
                </li>
                <li>
                  • With operator:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    @line1 * 1.1
                  </code>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Number formats
              </h4>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>
                  • Indian:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">2L</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">2 lakh</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">3C</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">3 crore</code>
                </li>
                <li>
                  • Western:{' '}
                  <code className="bg-white px-2 py-0.5 rounded">10K</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    10 thousand
                  </code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">5M</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    5 million
                  </code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">1.5B</code>
                  {', '}
                  <code className="bg-white px-2 py-0.5 rounded">
                    1.5 billion
                  </code>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-blue-200">
            <h4 className="font-semibold text-gray-900 mb-2">Miscellaneous</h4>
            <ul className="text-sm text-gray-700 space-y-1 grid md:grid-cols-2">
              <li>
                •{' '}
                <kbd className="bg-white px-2 py-0.5 rounded border">Enter</kbd>{' '}
                - New line
              </li>
              <li>
                •{' '}
                <kbd className="bg-white px-2 py-0.5 rounded border">
                  Backspace
                </kbd>{' '}
                (empty line) - Delete line
              </li>
              <li>
                • <kbd className="bg-white px-2 py-0.5 rounded border">↑</kbd> /{' '}
                <kbd className="bg-white px-2 py-0.5 rounded border">↓</kbd> -
                Navigate lines
              </li>
              <li>
                • Auto-saves to browser storage (your data is not shared with
                us)
              </li>
            </ul>
          </div>
        </div>

        {/* Toast Notification */}
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <div
              className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
                toast.type === 'success'
                  ? 'bg-green-600 text-white'
                  : toast.type === 'error'
                    ? 'bg-red-600 text-white'
                    : 'bg-blue-600 text-white'
              }`}
            >
              <span className="text-sm font-medium">{toast.message}</span>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default TextCalci;
